<template>
  <div class="ragout-container">
    <div class="content-section">
      <h1>Genome Assembly Scaffolding (Ragout)</h1>
      
      <div class="description">
        <h2>About Ragout</h2>
        <p>Ragout (Reference-Assisted Genome Ordering and Completion) is a tool for chromosome assembly using multiple references. It can improve draft assemblies using multiple closely related genomes as references.</p>
        
        <div class="features">
          <h3>Key Features:</h3>
          <ul>
            <li>Improving draft genome assemblies</li>
            <li>Chromosome-level scaffolding</li>
            <li>Comparative genomic analysis</li>
            <li>Evolutionary studies</li>
          </ul>
        </div>

        <div class="processing-note">
          <el-icon><info-filled /></el-icon>
          Processing Time Note
          <p>Analysis may take several minutes to hours depending on genome sizes and number of references.</p>
        </div>
      </div>

      <section>
        <h2>Upload Genome Files</h2>
        
        <!-- Target Genome Upload -->
        <div class="upload-area">
          <div class="upload-box">
            <h3>Target Genome</h3>
            <p class="upload-info">Upload the draft genome to be scaffolded (FASTA format: .fa, .fasta). Maximum file size: 500MB.</p>
            
            <el-upload
              class="upload-demo"
              drag
              action="/api/tools/ragout/upload-target/"
              :on-success="handleTargetSuccess"
              :on-error="handleError"
              :before-upload="beforeUploadTarget"
              :on-progress="handleTargetProgress"
              :multiple="false"
              :limit="1"
              :show-file-list="true"
              :auto-upload="true"
            >
              <el-icon class="el-icon--upload"><upload-filled /></el-icon>
              <div class="el-upload__text">
                Drop target genome file here or <em>click to upload</em>
              </div>
              <template #tip>
                <div class="el-upload__tip">
                  Target genome file (max 500MB)
                  <div v-if="targetFile">Current target file: {{ targetFile.filename }}</div>
                </div>
              </template>
            </el-upload>
          </div>

          <!-- Reference Genomes Upload -->
          <div class="upload-box">
            <h3>Reference Genomes</h3>
            <p class="upload-info">Upload one or more reference genomes (FASTA format: .fa, .fasta). Maximum file size per genome: 500MB.</p>
            
            <el-upload
              class="upload-demo"
              drag
              action="/api/tools/ragout/upload-references/"
              :data="{ job_id: targetFile?.job_id }"
              :on-success="handleReferenceSuccess"
              :on-error="handleError"
              :before-upload="beforeUploadReference"
              :on-progress="handleReferenceProgress"
              :multiple="true"
              :limit="5"
              :disabled="!targetFile?.job_id"
              :show-file-list="true"
              :auto-upload="true"
            >
              <el-icon class="el-icon--upload"><upload-filled /></el-icon>
              <div class="el-upload__text">
                <template v-if="!targetFile?.job_id">
                  Please upload target genome first
                </template>
                <template v-else>
                  Drop reference genome files here or <em>click to upload</em>
                </template>
              </div>
              <template #tip>
                <div class="el-upload__tip">
                  Reference genome files (max 5 files, 500MB each)
                  <br>
                  <template v-if="!targetFile?.job_id">
                    <span style="color: #f56c6c">Please upload target genome first</span>
                  </template>
                  <template v-else>
                    <div>Current job ID: {{ targetFile.job_id }}</div>
                    <div>Reference files: {{ referenceFiles.length }}</div>
                  </template>
                </div>
              </template>
            </el-upload>
          </div>
        </div>

        <!-- Parameters Section -->
        <div class="parameters-section">
          <h3>Analysis Parameters</h3>
          
          <el-form :model="parameters" label-width="180px">
            <el-form-item label="Synteny block size">
              <el-input-number
                v-model="parameters.syntenyBlockSize"
                :min="500"
                :max="10000"
                :step="100"
              />
              <span class="parameter-description">Minimum synteny block size (default: 5000)</span>
            </el-form-item>

            <el-form-item label="Minimum contig length">
              <el-input-number
                v-model="parameters.minContigLength"
                :min="0"
                :max="10000"
                :step="100"
              />
              <span class="parameter-description">Minimum contig length in assembly (default: 500)</span>
            </el-form-item>

            <el-form-item label="Phylogeny mode">
              <el-select v-model="parameters.phylogenyMode">
                <el-option label="Simple" value="simple" />
                <el-option label="Complex" value="complex" />
              </el-select>
              <span class="parameter-description">Method for phylogenetic tree construction</span>
            </el-form-item>
          </el-form>

          <div class="submit-section">
            <el-button 
              type="primary" 
              @click="startAnalysis"
              :disabled="!canStartAnalysis"
              :loading="analyzing"
              size="large"
            >
              Start Analysis
            </el-button>
          </div>
        </div>

        <!-- Progress Display -->
        <div v-if="analyzing" class="status-section">
          <div class="progress-section">
            <el-progress 
              :percentage="analysisProgress" 
              :status="analysisProgress === 100 ? 'success' : ''"
            />
            <p class="progress-text">{{ progressText }}</p>
          </div>
        </div>
      </section>

      <!-- Results Display Section -->
      <div v-if="results" class="results-section">
        <div class="results-header">
          <h2>Analysis Results</h2>
          <el-button 
            type="primary" 
            @click="downloadResults" 
            :disabled="!resultFileUrl"
            :loading="downloading"
            size="large"
          >
            Download Results
          </el-button>
        </div>

        <el-descriptions
          title="Scaffolding Summary"
          :column="2"
          border
        >
          <el-descriptions-item label="Input Contigs">
            {{ results.inputContigs }}
          </el-descriptions-item>
          <el-descriptions-item label="Output Scaffolds">
            {{ results.outputScaffolds }}
          </el-descriptions-item>
          <el-descriptions-item label="N50 Before">
            {{ results.n50Before }}
          </el-descriptions-item>
          <el-descriptions-item label="N50 After">
            {{ results.n50After }}
          </el-descriptions-item>
          <el-descriptions-item label="Total Length">
            {{ results.totalLength }}
          </el-descriptions-item>
          <el-descriptions-item label="Gaps Introduced">
            {{ results.gapsIntroduced }}
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </div>
    
    <!-- Citation and License Section -->
    <div class="citation-section">
      <h3>Citation and License</h3>
      <div class="citation-content">
        <p class="citation-text">
          This tool integrates Ragout, developed by Kolmogorov et al. If you use this tool in your research, please cite:
        </p>
        <div class="citation-box">
          <p class="paper-citation">
            <strong>Primary Citation:</strong><br>
            Kolmogorov M., Raney B., Paten B., Pham S. (2014). 
            Ragout—a reference-assisted assembly tool for bacterial genomes. 
            <em>Bioinformatics</em>, 30(12): i302-i309.
            <a href="https://doi.org/10.1093/bioinformatics/btu280" target="_blank">https://doi.org/10.1093/bioinformatics/btu280</a>
          </p>
          <p class="repo-citation">
            <strong>Software Repository:</strong><br>
            Ragout GitHub Repository: <a href="https://github.com/fenderglass/Ragout" target="_blank">https://github.com/fenderglass/Ragout</a>
          </p>
        </div>
        <div class="license-info">
          <p class="license-text">
            Ragout is licensed under the MIT License.
          </p>
          <p class="usage-note">
            This is an integration of Ragout and is not officially affiliated with the Ragout project. For the official implementation and latest updates, please visit the Ragout GitHub repository.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { UploadFilled, InfoFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'

export default {
  name: 'RagoutTool',
  components: {
    UploadFilled,
    InfoFilled
  },
  setup() {
    const targetFile = ref(null)
    const referenceFiles = ref([])
    const analyzing = ref(false)
    const analysisProgress = ref(0)
    const progressText = ref('')
    const results = ref(null)
    const resultFileUrl = ref(null)
    const downloading = ref(false)

    const parameters = ref({
      syntenyBlockSize: 5000,
      minContigLength: 500,
      phylogenyMode: 'simple'
    })

    const canStartAnalysis = computed(() => {
      return targetFile.value?.job_id && referenceFiles.value.length > 0
    })

    const beforeUploadTarget = (file) => {
      const isValidType = ['fa', 'fasta'].includes(
        file.name.split('.').pop().toLowerCase()
      )
      const isLt500M = file.size / 1024 / 1024 < 500

      if (!isValidType) {
        ElMessage.error('Please upload a FASTA format file!')
        return false
      }
      if (!isLt500M) {
        ElMessage.error('File size cannot exceed 500MB!')
        return false
      }
      return true
    }

    const beforeUploadReference = (file) => {
      console.log('Before upload reference file:', file)
      console.log('Current targetFile:', targetFile.value)
      if (!targetFile.value?.job_id) {
        ElMessage.warning('Please upload target genome first')
        return false
      }
      return beforeUploadTarget(file)
    }

    const handleTargetSuccess = (response, file) => {
      console.log('Target upload response:', response)
      console.log('Target file:', file)
      console.log('Current targetFile value:', targetFile.value)
      
      targetFile.value = {
        filename: response.filename,
        job_id: response.job_id
      }
      
      console.log('New targetFile value:', targetFile.value)
      ElMessage.success('Target genome uploaded successfully')
    }

    const handleReferenceSuccess = (response, file) => {
      console.log('Reference upload response:', response)
      console.log('Reference file:', file)
      console.log('Current referenceFiles:', referenceFiles.value)
      
      referenceFiles.value.push({
        filename: response.filename,
        job_id: response.job_id
      })
      ElMessage.success('Reference genome uploaded successfully')
    }

    const handleError = (error, file) => {
      console.error('Upload error:', error)
      console.error('File that caused error:', file)
      ElMessage.error('Upload failed: ' + (error.message || 'Unknown error'))
    }

    const handleTargetProgress = () => {
      // Handle upload progress
    }

    const handleReferenceProgress = () => {
      // Handle upload progress
    }

    const startAnalysis = async () => {
      analyzing.value = true
      analysisProgress.value = 0
      progressText.value = 'Starting analysis...'

      try {
        const response = await fetch('/api/tools/ragout/analyze/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            job_id: targetFile.value.job_id,
            parameters: parameters.value
          })
        })

        if (response.ok) {
          const data = await response.json()
          results.value = data
          resultFileUrl.value = data.resultFile
          ElMessage.success('Analysis completed successfully')
        } else {
          throw new Error('Analysis failed')
        }
      } catch (error) {
        ElMessage.error('Analysis failed: ' + error.message)
      } finally {
        analyzing.value = false
      }
    }

    const downloadResults = async () => {
      if (!targetFile.value?.job_id) {
        ElMessage.error('没有可下载的结果文件');
        return
      }

      try {
        const response = await axios.get(`/api/tools/ragout/download/${targetFile.value.job_id}/`, {
          responseType: 'blob'
        });
        
        // 创建下载链接
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ragout_results_${targetFile.value.job_id}.zip`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        ElMessage.success('结果文件下载成功');
      } catch (error) {
        console.error('下载错误:', error);
        ElMessage.error('下载失败：' + error.message);
      }
    };

    return {
      targetFile,
      referenceFiles,
      parameters,
      analyzing,
      analysisProgress,
      progressText,
      results,
      resultFileUrl,
      downloading,
      canStartAnalysis,
      beforeUploadTarget,
      beforeUploadReference,
      handleTargetSuccess,
      handleReferenceSuccess,
      handleError,
      handleTargetProgress,
      handleReferenceProgress,
      startAnalysis,
      downloadResults
    }
  }
}
</script>

<style scoped>
.ragout-container {
  padding: 20px;
}

.content-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
}

h1 {
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
}

.description {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.description h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.description p {
  color: #666;
  line-height: 1.6;
}

.features {
  margin-top: 20px;
}

.features h3 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.2rem;
}

.features ul {
  list-style-type: none;
  padding-left: 0;
}

.features li {
  color: #666;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.features li::before {
  content: "•";
  color: #4169E1;
  position: absolute;
  left: 0;
}

.upload-area {
  display: flex;
  gap: 20px;
  margin: 20px 0;
}

.upload-box {
  flex: 1;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.upload-box h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #2c3e50;
}

.upload-info {
  color: #666;
  margin-bottom: 20px;
}

.parameters-section {
  margin: 20px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.submit-section {
  margin: 20px 0;
  text-align: center;
}

.status-section {
  margin: 20px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.progress-section {
  margin-top: 20px;
}

.progress-text {
  text-align: center;
  color: #666;
  margin-top: 10px;
}

.results-section {
  margin-top: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.citation-section {
  margin-top: 40px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.citation-section h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.3rem;
}

.citation-content {
  color: #666;
  line-height: 1.6;
}

.citation-box {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
  margin: 10px 0;
}

.citation-box a {
  color: #409EFF;
  text-decoration: none;
  word-break: break-all;
}

.citation-box a:hover {
  text-decoration: underline;
}

.paper-citation {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px dashed #e4e7ed;
  line-height: 1.6;
}

.paper-citation em {
  font-style: italic;
}

.repo-citation {
  margin-bottom: 0;
  line-height: 1.6;
}

.citation-box strong {
  color: #2c3e50;
}

.license-info {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e4e7ed;
}

.license-text {
  margin-top: 10px;
  font-style: italic;
}

.usage-note {
  margin-top: 10px;
  font-size: 0.9em;
  color: #909399;
}

:deep(.el-upload) {
  width: 100%;
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

:deep(.el-upload-dragger .el-icon--upload) {
  margin: 0 0 10px;
}

:deep(.el-upload__text) {
  margin: 10px 0;
}

:deep(.el-upload__tip) {
  padding: 10px 0;
  text-align: center;
  font-size: 12px;
}

:deep(.el-icon--upload) {
  font-size: 48px;
}

.parameter-description {
  margin-left: 10px;
  color: #666;
  font-size: 0.9rem;
}

.processing-note {
  margin-top: 20px;
  padding: 15px;
  background: #f0f9ff;
  border-radius: 4px;
  color: #1d4ed8;
}

.processing-note .el-icon {
  margin-right: 8px;
  vertical-align: middle;
}
</style> 